export interface LoginRequestPayload {
  email: string;
  password: string;
}

interface UserInfo {
  email?: string;
  id: string;
  isAdmin: boolean;
  nickname: string | null;
  phone: string | null;
  tenantId: number | null;
}

export interface LoginResponse {
  accessToken: string;
  isRfid: boolean;
  user: UserInfo;
}

interface LoginApiResponse {
  accessToken?: string;
  refreshToken?: string;
  userId?: number | string;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  isPassgen?: boolean;
  isRfid?: boolean;
  machines?: unknown;
}

const API_BASE_URL = (process.env.REACT_APP_USER_MGMT_API_BASE_URL ?? "http://127.0.0.1:9821").replace(/\/+$/, "");

const MAESTRO_PRODUCT_ID = 2;

const coerceTenantId = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const parsed = typeof value === "number" ? value : Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const base64UrlDecode = (input: string): string => {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
  return atob(padded);
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = base64UrlDecode(parts[1]);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const buildRolesFromClaims = (payload: Record<string, unknown> | null): Array<{ name: string }> => {
  const roleNames = Array.isArray(payload?.roles) ? (payload?.roles as unknown[]) : [];
  return roleNames.filter((role): role is string => typeof role === "string" && role.length > 0).map((name) => ({ name }));
};

const isAdminFromClaims = (payload: Record<string, unknown> | null, roles: Array<{ name: string }>): boolean => {
  const adminNames = new Set(["super_admin", "admin", "tenant_admin"]);
  if (roles.some((role) => adminNames.has(role.name))) return true;
  const permissions = Array.isArray(payload?.permissions) ? (payload?.permissions as unknown[]) : [];
  return permissions.some((permission) => permission === "admin" || permission === "super_admin");
};

const userFromLoginPayload = (loginData: LoginApiResponse, payload: Record<string, unknown> | null): UserInfo => {
  const roles = buildRolesFromClaims(payload);
  const displayName = [loginData.firstName, loginData.lastName]
    .filter((part): part is string => typeof part === "string" && part.trim() !== "")
    .join(" ")
    .trim();

  const tenantId = coerceTenantId(payload?.tenantId) ?? coerceTenantId(payload?.tenant_id);
  const loginId = loginData.userId ?? payload?.sub;
  const resolvedId = loginId === undefined || loginId === null ? "" : String(loginId);

  return {
    email: loginData.email,
    id: resolvedId,
    isAdmin: isAdminFromClaims(payload, roles),
    nickname: displayName || null,
    phone: null,
    tenantId
  };
};

const auth = {
  login: async (payload: LoginRequestPayload): Promise<LoginResponse> => {
    const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: payload.email,
        password: payload.password,
        productId: MAESTRO_PRODUCT_ID
      })
    });

    const loginData = (await loginResponse.json().catch(() => ({}))) as LoginApiResponse;
    if (!loginResponse.ok) {
      throw new Error((loginData as { message?: string; error?: string }).message ?? (loginData as { error?: string }).error ?? "Login failed");
    }

    if (loginData.isPassgen !== true) {
      throw new Error("Permission not allowed");
    }

    const accessToken = loginData.accessToken;
    const refreshToken = loginData.refreshToken;
    if (accessToken === undefined || accessToken === "" || refreshToken === undefined || refreshToken === "") {
      throw new Error("Login succeeded but tokens are missing in response");
    }

    const jwtPayload = decodeJwtPayload(accessToken);

    return {
      accessToken,
      isRfid: loginData.isRfid === true,
      user: userFromLoginPayload(loginData, jwtPayload)
    };
  }
};

export default auth;
